import React, { Suspense, lazy, useEffect, useState } from "react";
import Slider from "react-slick";
import VideoWithPoster from "../common/VideoWithPoster";
import { Helmet } from "react-helmet-async";
import MainBanner from "./MainBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, Button } from "react-bootstrap";
import googleMap from "../images/Rosalin-Child-Development-Centre-location.png";
import video4 from "../videos/Visual_Perceptual_Skill_Training.mp4";

const HomeTestimonialsSection = lazy(() => import("./HomeTestimonialsSection"));

const ContentIndex = () => {
  const canonicalUrl = "https://www.rosalinchilddevelopment.com";
  const [width, setWidth] = useState(window.innerWidth);
  const [instaPosts, setInstaPosts] = useState([]);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    fetch("https://www.rosalinchilddevelopment.com/administration/home/b2c_instagram")
      .then((res) => res.json())
      .then((data) => setInstaPosts(data))
      .catch(() => {});
  }, []);

  return (
    <div>
      <Helmet>
        <title>Child Development Centre in Lucknow | Rosalin CDC</title>
        <meta
          name="description"
          content="Rosalin Child Development Centre in Lucknow offers personalised therapy for speech, occupational, behavioural, and developmental needs. Book an assessment today."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="google-site-verification"
          content="sF8jEG16KPd-hnUJipuxbVJ0M4iJtkDDym4DxoAmPdY"
        />
        <meta property="og:image" content="https://www.rosalinchilddevelopment.com/rosalinchilddevelopment-hero-1.webp" />
      </Helmet>
      <MainBanner width={width} />

      {/* Because Every Small Step Matters */}
      <div className="everySmallStep">
        <div className="container">
          <h2 className="ttlMain">Because Every Small Step Matters</h2>
          <p>
            Every child learns differently. Some need a little extra support to communicate, focus,
            interact, or become more independent. Early guidance can make a meaningful difference in
            helping children develop the skills they need for everyday life.
          </p>
          <p>
            At Rosalin Child Development Centre, we believe therapy should be more than
            sessions&#x2014;it should be a partnership with families. Our therapists work closely with
            parents to understand each child&#39;s strengths, challenges, and goals, creating
            personalised therapy plans that encourage steady and meaningful progress.
          </p>
          <p>
            We celebrate every achievement, no matter how small, because every step forward is a
            step toward a brighter future.
          </p>
        </div>
      </div>

      {/* Our Therapy Services */}
      <div className="ourSerInd">
        <div className="container">
          <h2 className="ttlMain">Our Therapy Services</h2>
          <p className="secSubtitle">
            We offer comprehensive therapy services under one roof to support children with
            different developmental needs.
          </p>
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb1"></div>
                <h3><a href="/speech-therapy-in-lucknow">Speech Therapy</a></h3>
                <p>
                  Helping children improve speech clarity, language development, communication
                  skills, and social interaction.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb2"></div>
                <h3><a href="/occupational-therapy-in-lucknow">Occupational Therapy</a></h3>
                <p>
                  Supporting children in developing fine motor skills, sensory processing, daily
                  living skills, handwriting, attention, and independence.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb3"></div>
                <h3><a href="/aba-therapy-in-lucknow">ABA Therapy</a></h3>
                <p>
                  Structured, evidence-informed therapy designed to improve communication,
                  learning, behaviour, and everyday functional skills.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb4"></div>
                <h3><a href="/services">Special Education</a></h3>
                <p>
                  Personalised learning support that helps children build academic confidence and
                  reach their educational potential.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb5"></div>
                <h3><a href="/behavioral-therapy-in-lucknow">Behavioral Therapy</a></h3>
                <p>
                  Helping children understand emotions, improve self-control, reduce challenging
                  behaviours, and develop positive social skills.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb6"></div>
                <h3><a href="/services">Early Intervention</a></h3>
                <p>
                  Providing timely support for infants and young children to encourage healthy
                  developmental progress during the most important early years.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb7"></div>
                <h3><a href="/services">Physiotherapy</a></h3>
                <p>
                  Supporting children experiencing physical or motor development challenges
                  improving strength, balance, posture, coordination, and gross motor skills.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="icoTtlBlo">
                <div className="itbIcon itb8"></div>
                <h3><a href="/services">Sensory Integration Therapy</a></h3>
                <p>
                  Helping children better understand and respond to sensory input, improving
                  attention, emotional regulation, and daily functioning.
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <Button variant="primary" size="lg" href="/services">
              Explore All Services
            </Button>
          </div>
        </div>
      </div>

      {/* Conditions We Support */}
      <div className="accConAdd">
        <div className="container">
          <div className="row align-items-center">
            {width > 768 && (
              <div className="col-lg-6">
                <div className="youtube16">
                  <VideoWithPoster src={video4} />
                </div>
              </div>
            )}
            <div className={width > 768 ? "col-lg-6" : "col-12"}>
              <h2 className="ttl">
                Conditions <span>We Support</span>
              </h2>
              <p>
                Our multidisciplinary team provides therapy and support for children experiencing:
              </p>
              <ul className="condList">
                <li><FontAwesomeIcon icon="angle-right" /> Autism Spectrum Disorder (ASD)</li>
                <li><FontAwesomeIcon icon="angle-right" /> ADHD</li>
                <li><FontAwesomeIcon icon="angle-right" /> Speech &amp; Language Delay</li>
                <li><FontAwesomeIcon icon="angle-right" /> Developmental Delay</li>
                <li><FontAwesomeIcon icon="angle-right" /> Learning Disabilities</li>
                <li><FontAwesomeIcon icon="angle-right" /> Sensory Processing Difficulties</li>
                <li><FontAwesomeIcon icon="angle-right" /> Cerebral Palsy</li>
                <li><FontAwesomeIcon icon="angle-right" /> Down Syndrome</li>
                <li><FontAwesomeIcon icon="angle-right" /> Intellectual Disabilities</li>
                <li><FontAwesomeIcon icon="angle-right" /> Social &amp; Communication Challenges</li>
                <li><FontAwesomeIcon icon="angle-right" /> Behavioral Difficulties</li>
                <li><FontAwesomeIcon icon="angle-right" /> Fine &amp; Gross Motor Delays</li>
              </ul>
              <p className="mt-3">
                Every child receives an individualised therapy plan based on their unique needs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Why Parents Trust Rosalin */}
      <div className="ourInno whyParentsTrust">
        <div className="container">
          <h2 className="ttlMain">Why Parents Trust Rosalin</h2>
          <div className="row mt-4">
            <div className="col-md-4 trustItem">
              <h4>Personalised Therapy Plans</h4>
              <p>
                No two children are alike. Every therapy plan is designed around your child&#39;s
                strengths, needs, and developmental goals.
              </p>
            </div>
            <div className="col-md-4 trustItem">
              <h4>Experienced Professionals</h4>
              <p>
                Our multidisciplinary team works together to provide coordinated care that supports
                your child&#39;s overall development.
              </p>
            </div>
            <div className="col-md-4 trustItem">
              <h4>Parent-Centered Approach</h4>
              <p>
                Parents play an important role in every child&#39;s journey. We guide, educate, and
                involve families throughout the therapy process.
              </p>
            </div>
            <div className="col-md-4 trustItem">
              <h4>Child-Friendly Environment</h4>
              <p>
                Our therapy spaces are designed to help children feel comfortable, engaged, and
                motivated to learn.
              </p>
            </div>
            <div className="col-md-4 trustItem">
              <h4>Progress That Matters</h4>
              <p>
                We focus on meaningful improvements that help children become more confident and
                independent in everyday life.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Therapy Process */}
      <div className="therapyProcess">
        <div className="container">
          <h2 className="ttlMain">Our Therapy Process</h2>
          <div className="row mt-4">
            <div className="col-md processStep">
              <div className="stepNum">1</div>
              <h4>Book an Initial Assessment</h4>
              <p>
                Meet our specialists to discuss your child&#39;s developmental concerns and understand
                their current abilities.
              </p>
            </div>
            <div className="col-md processStep">
              <div className="stepNum">2</div>
              <h4>Comprehensive Evaluation</h4>
              <p>
                We carefully assess communication, behaviour, learning, sensory needs, motor
                skills, and overall development.
              </p>
            </div>
            <div className="col-md processStep">
              <div className="stepNum">3</div>
              <h4>Personalised Therapy Plan</h4>
              <p>
                Based on the assessment, we create an individualised therapy plan with achievable
                goals.
              </p>
            </div>
            <div className="col-md processStep">
              <div className="stepNum">4</div>
              <h4>Regular Therapy Sessions</h4>
              <p>
                Children receive engaging, structured therapy while parents receive continuous
                guidance and support.
              </p>
            </div>
            <div className="col-md processStep">
              <div className="stepNum">5</div>
              <h4>Monitor Progress</h4>
              <p>
                We regularly review your child&#39;s development and adjust therapy plans to ensure
                continued growth.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      {width > 768 && (
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          <HomeTestimonialsSection />
        </Suspense>
      )}

      {/* What Parents Often Notice */}
      <div className="parentNotice">
        <div className="container">
          <h2 className="ttlMain">What Parents Often Notice</h2>
          <div className="row">
            <div className="col-md-6">
              <ul className="noticeList">
                <li><FontAwesomeIcon icon="angle-right" /> Delayed speech or language development</li>
                <li><FontAwesomeIcon icon="angle-right" /> Limited eye contact or social interaction</li>
                <li><FontAwesomeIcon icon="angle-right" /> Difficulty understanding instructions</li>
                <li><FontAwesomeIcon icon="angle-right" /> Hyperactivity or trouble focusing</li>
                <li><FontAwesomeIcon icon="angle-right" /> Challenges with learning or academics</li>
              </ul>
            </div>
            <div className="col-md-6">
              <ul className="noticeList">
                <li><FontAwesomeIcon icon="angle-right" /> Sensory sensitivities</li>
                <li><FontAwesomeIcon icon="angle-right" /> Difficulty with daily activities</li>
                <li><FontAwesomeIcon icon="angle-right" /> Repetitive behaviors</li>
                <li><FontAwesomeIcon icon="angle-right" /> Poor fine motor or handwriting skills</li>
              </ul>
            </div>
          </div>
          <p className="mt-3">
            If you&#39;re concerned about your child&#39;s development, an assessment is a helpful first step.
          </p>
          <div className="text-center mt-3">
            <Button variant="primary" size="lg" href="/book-appointment">
              Book an Assessment
            </Button>
          </div>
        </div>
      </div>

      {/* Instagram Posts */}
      {instaPosts.length > 0 && (
        <div className="instagramSection">
          <div className="container">
            <Slider
              dots={false}
              infinite={true}
              speed={500}
              slidesToShow={5}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={2500}
              pauseOnHover={true}
              arrows={true}
              responsive={[
                { breakpoint: 1199, settings: { slidesToShow: 4 } },
                { breakpoint: 991,  settings: { slidesToShow: 3 } },
                { breakpoint: 767,  settings: { slidesToShow: 2 } },
                { breakpoint: 480,  settings: { slidesToShow: 2 } },
              ]}
              className="instaSlider mt-4"
            >
              {instaPosts.map((post, index) => (
                <div key={index} className="instaSlide">
                  <div className="instaItem">
                    <img
                      src={`https://www.rosalinchilddevelopment.com/resource/images/instagram/${post.content}`}
                      alt="Rosalin Child Development Centre Instagram"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}

      {/* Frequently Asked Questions */}
      <div className="faqSection">
        <div className="container">
          <h2 className="ttlMain">Frequently Asked Questions</h2>
          <Accordion className="accTra mt-4">
            <Accordion.Item eventKey="0">
              <Accordion.Header>How do I know if my child needs therapy?</Accordion.Header>
              <Accordion.Body>
                If you notice delays in speech, learning, social interaction, behaviour, or daily
                skills, it&#39;s a good idea to consult a specialist for an assessment.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>What age do you work with?</Accordion.Header>
              <Accordion.Body>
                We support infants, toddlers, preschoolers, school-age children, and adolescents,
                depending on their individual needs.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>How long does therapy take?</Accordion.Header>
              <Accordion.Body>
                Every child progresses differently. Therapy duration depends on the child&#39;s goals,
                needs, and response to intervention.
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>Can parents participate in therapy?</Accordion.Header>
              <Accordion.Body>
                Yes. We believe parents are an essential part of the therapy process and encourage
                active involvement.
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
      </div>

      {/* Map */}
      {width > 768 && (
        <div className="gooMap">
          <a
            href="https://maps.app.goo.gl/pwRpC34aLSwzPni77"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={googleMap}
              alt="Rosalin Child Development Centre location map"
              loading="lazy"
              width="2844"
              height="904"
            />
          </a>
        </div>
      )}
    </div>
  );
};

export default ContentIndex;
